'use client';

import { FC, useEffect, useState } from 'react';
import {
  alignContent,
  getFigurePaddingTopWithMaxHeight,
  heroDescriptionPrimaryFields,
  isNullish,
  resolveLink,
  secondaryCtaFields,
  toCtaButtonPropsFromItem,
} from '@/utils';
import { PrismicNextLink } from '@prismicio/next';
import {
  Container,
  CtaActionsRow,
  CtaButton,
  IconSvg,
  PrismicImageWithAlt,
} from '@/components';
import { useSelectedStore, useSliceItemsForSelectedStore } from '@/hooks';
import { Swiper as ISwiper } from 'swiper/types';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  HeroImageSlice,
  HeroImageSliceHeroImageWithHeadingAndDescripItem,
} from '@/types';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import classNames from 'classnames';
import { asText, ImageField } from '@prismicio/client';
import style from './heroimage.module.scss';

type HeroImageProps = HeroImageSlice & {
  alternateItems?: HeroImageSlice['items'];
  initialStoreUrlSlug?: string;
};

const HeroImage: FC<HeroImageProps> = ({
  alternateItems,
  initialStoreUrlSlug,
  ...slice
}) => {
  const { store } = useSelectedStore();
  const [isMounted, setIsMounted] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number | null>(
    null,
  );

  const items = useSliceItemsForSelectedStore({
    alternateItems,
    initialStoreUrlSlug,
    items: slice.items ?? [],
    primaryLocationSlug: slice.primary?.location_slug,
  });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!items.length) {
    return null;
  }

  const backgroundColor = slice?.primary?.background_color ?? undefined;
  const fullWidth = slice?.primary?.full_width;
  const imgEffect = slice?.primary?.effect_zoom_image;
  const withousVerticalSpace = slice?.primary?.withous_vertical_space;
  const maxHeigthDesktop = slice?.primary?.max_height_desktop ?? 0;
  const maxHeigthMobile = slice?.primary?.max_height_mobile ?? 0;
  const adjustAspectRatioDesktop = slice?.primary?.adjust_aspect_ratio_desktop;
  const adjustAspectRatioMobile = slice?.primary?.adjust_aspect_ratio_mobile;
  const bottomSpacing = slice?.primary?.bottom_spacing;
  const shouldAdjustAspectRatio =
    adjustAspectRatioDesktop || adjustAspectRatioMobile;

  const sizes = `(min-width: 2048px)  2008px, (min-width: 1536px)  1496px,
        (min-width: 1280px)  1240px, (min-width: 1024px) 984px,
        (min-width: 768px) 728px, 100vw`;

  const isVariationWithHeading =
    slice?.variation === 'heroImageWithHeadingAndDescrip';

  const isSlideVisible = (index: number) =>
    (!isMounted && index === 0) || (isMounted && !isNullish(currentSlideIndex));

  const handleSwiperInit = (swiper: ISwiper) => {
    setCurrentSlideIndex(swiper.realIndex);
  };

  const handleSlideChange = (swiper: ISwiper) => {
    setCurrentSlideIndex(swiper.realIndex);
  };

  const renderImage = (
    banner: ImageField | null | undefined,
    imageSizes: string,
    classCss: string,
    adjustAspectRatio?: boolean,
    maxHeight?: number | null,
  ) => {
    const paddingTop = getFigurePaddingTopWithMaxHeight(
      banner,
      adjustAspectRatio,
      maxHeight,
    );

    return (
      <figure
        className={classCss}
        style={{
          ...(paddingTop && { paddingTop }),
        }}
      >
        {banner?.url ? (
          <PrismicImageWithAlt
            fetchPriority="high"
            field={banner}
            fill
            loading="eager"
            priority
            sizes={imageSizes}
            style={{ objectFit: 'cover', objectPosition: 'center center' }}
          />
        ) : (
          <IconSvg name="image" />
        )}
      </figure>
    );
  };

  const renderClickableHeroImage = (
    banner: HeroImageSliceHeroImageWithHeadingAndDescripItem,
  ) => {
    const resolved = resolveLink(
      banner.image_link,
      banner.image_external_link,
      { externalNewTab: false },
    );
    const opensNewTab =
      'target' in resolved.props && resolved.props.target === '_blank';
    const destinationLabel = banner.image_link?.trim() || 'home';
    const ariaLabel = opensNewTab
      ? 'Hero image (opens in a new tab)'
      : `Go to ${destinationLabel}`;

    const images = (
      <>
        {renderImage(
          banner?.hero_image,
          sizes,
          style.banner__figure,
          adjustAspectRatioDesktop,
          maxHeigthDesktop,
        )}
        {renderImage(
          banner?.hero_image?.mobile,
          '(min-width: 640px) 610px, 100wv',
          style.banner__figure_mobile,
          adjustAspectRatioMobile,
          maxHeigthMobile,
        )}
      </>
    );

    if (resolved.kind === 'empty') {
      return (
        <PrismicNextLink aria-label="Go to home" href="/">
          {images}
        </PrismicNextLink>
      );
    }

    return (
      <PrismicNextLink {...resolved.props} aria-label={ariaLabel}>
        {images}
      </PrismicNextLink>
    );
  };

  return (
    <section
      className={classNames(style.hero, {
        [style.hero__spacing_bottom]: bottomSpacing,
      })}
      key={store?.url_slug ?? initialStoreUrlSlug ?? 'default'}
      style={{
        backgroundColor,
        ...(maxHeigthMobile && {
          '--hero-max-height-mobile': `${maxHeigthMobile}px`,
        }),
        ...(maxHeigthDesktop && {
          '--hero-max-height-desktop': `${maxHeigthDesktop}px`,
        }),
        ...(!fullWidth && {
          '--hero-border-radius': `${slice?.primary?.border_radius ?? 0}px`,
        }),
      }}
    >
      <Container
        className={classNames(style.container__clear, {
          [style.container__full_width]: fullWidth,
          [style.container__withous_margin]: withousVerticalSpace,
          [style.container__with_margin]: !withousVerticalSpace,
          [style.container__effect_img]: imgEffect,
        })}
      >
        <Swiper
          autoHeight={shouldAdjustAspectRatio}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          className={style.swipper__hero_image}
          effect="fade"
          loop
          modules={[Autoplay, EffectFade, Navigation, Pagination]}
          onAfterInit={handleSwiperInit}
          onSlideChange={handleSlideChange}
          pagination={{
            clickable: true,
            renderBullet: (index, className) =>
              `<span class="${className}" role="button" tabindex="0" aria-label="Go to slide ${
                index + 1
              }"></span>`,
          }}
          slidesPerView={1}
        >
          {items.map(
            (
              banner: HeroImageSliceHeroImageWithHeadingAndDescripItem,
              index,
            ) => {
              const HeadingTag =
                banner?.title?.[0]?.type === 'heading1' ? 'h1' : 'h2';
              const heading = asText(banner?.heading_text);
              const headingColor = banner?.heading_color as string;
              const title = asText(banner?.title);
              const titleColor = banner?.title_color as string;
              const description = asText(banner?.description);
              const descriptionColor = banner?.description_color as string;
              const primaryCta = toCtaButtonPropsFromItem(
                banner,
                heroDescriptionPrimaryFields,
                'primary',
              );
              const secondaryCta = toCtaButtonPropsFromItem(
                banner,
                secondaryCtaFields,
                'secondary',
              );
              const hasDescriptionContent =
                heading || title || description || primaryCta || secondaryCta;

              return (
                <SwiperSlide key={index}>
                  {isSlideVisible(index) && (
                    <>
                      {isVariationWithHeading && hasDescriptionContent && (
                        <Container
                          className={style.container__content}
                          style={{
                            alignItems: alignContent(
                              banner?.horizontal_position ?? 'start',
                            ),
                            justifyContent: alignContent(
                              banner?.vertical_position ?? 'start',
                            ),
                            ...(banner?.content_max_width && {
                              maxWidth: `${banner.content_max_width}%`,
                            }),
                            textAlign: banner?.text_alignment ?? 'left',
                          }}
                        >
                          <div
                            className={style.content__description}
                            style={{
                              alignItems: alignContent(
                                banner?.horizontal_position ?? 'start',
                              ),
                              textAlign: banner?.text_alignment ?? 'left',
                            }}
                          >
                            {heading && (
                              <p
                                className={style.content__description__heading}
                                dangerouslySetInnerHTML={{
                                  __html: heading,
                                }}
                                style={{ color: headingColor }}
                              />
                            )}
                            {title && (
                              <HeadingTag
                                className={style.content__description__header}
                                dangerouslySetInnerHTML={{
                                  __html: title,
                                }}
                                style={{ color: titleColor }}
                              />
                            )}
                            {description && (
                              <p
                                className={style.content__description__descrip}
                                dangerouslySetInnerHTML={{
                                  __html: description,
                                }}
                                style={{
                                  color: descriptionColor,
                                }}
                              />
                            )}
                            {(primaryCta || secondaryCta) && (
                              <CtaActionsRow
                                className={style.content__description__actions}
                              >
                                {primaryCta && (
                                  <CtaButton
                                    className={
                                      style.content__description__action
                                    }
                                    {...primaryCta}
                                  />
                                )}
                                {secondaryCta && (
                                  <CtaButton
                                    className={
                                      style.content__description__action
                                    }
                                    {...secondaryCta}
                                  />
                                )}
                              </CtaActionsRow>
                            )}
                          </div>
                        </Container>
                      )}
                      {renderClickableHeroImage(banner)}
                    </>
                  )}
                </SwiperSlide>
              );
            },
          )}
        </Swiper>
      </Container>
    </section>
  );
};

export default HeroImage;
